// Production monitoring utility
class ProductionMonitor {
    constructor() {
        this.isProduction = process.env.NODE_ENV === 'production';
        this.metrics = {
            pageViews: 0,
            exports: 0,
            errors: 0,
            apiCalls: 0
        };
        this.startTime = Date.now();
    }

    // Track page views
    trackPageView(pageName) {
        this.metrics.pageViews++;
        if (this.isProduction) {
            console.log(`[MONITORING] Page View: ${pageName}`);
            // In production, you could send this to analytics service
            // Example: analytics.track('page_view', { page: pageName });
        }
    }

    // Track export actions
    trackExport(type, success = true) {
        this.metrics.exports++;
        if (this.isProduction) {
            console.log(`[MONITORING] Export: ${type} - ${success ? 'SUCCESS' : 'FAILED'}`);
            // Example: analytics.track('export', { type, success });
        }
    }

    // Track API calls
    trackApiCall(endpoint, method, success = true, duration = 0) {
        this.metrics.apiCalls++;
        if (this.isProduction) {
            console.log(`[MONITORING] API Call: ${method} ${endpoint} - ${success ? 'SUCCESS' : 'FAILED'} (${duration}ms)`);
            // Example: analytics.track('api_call', { endpoint, method, success, duration });
        }
    }

    // Track errors
    trackError(error, context = '') {
        this.metrics.errors++;
        if (this.isProduction) {
            console.error(`[MONITORING] Error in ${context}:`, error);
            // Example: Sentry.captureException(error, { extra: { context } });
        }
    }

    // Track user actions
    trackUserAction(action, details = {}) {
        if (this.isProduction) {
            console.log(`[MONITORING] User Action: ${action}`, details);
            // Example: analytics.track('user_action', { action, ...details });
        }
    }

    // Track performance metrics
    trackPerformance(metric, value) {
        if (this.isProduction) {
            console.log(`[MONITORING] Performance: ${metric} = ${value}`);
            // Example: analytics.track('performance', { metric, value });
        }
    }

    // Get system uptime
    getUptime() {
        return Date.now() - this.startTime;
    }

    // Get metrics summary
    getMetrics() {
        return {
            ...this.metrics,
            uptime: this.getUptime()
        };
    }

    // Reset metrics (useful for testing)
    resetMetrics() {
        this.metrics = {
            pageViews: 0,
            exports: 0,
            errors: 0,
            apiCalls: 0
        };
        this.startTime = Date.now();
    }
}

// Create singleton instance
const productionMonitor = new ProductionMonitor();

export default productionMonitor;

// Convenience functions
export const trackPageView = (pageName) => productionMonitor.trackPageView(pageName);
export const trackExport = (type, success) => productionMonitor.trackExport(type, success);
export const trackApiCall = (endpoint, method, success, duration) => productionMonitor.trackApiCall(endpoint, method, success, duration);
export const trackError = (error, context) => productionMonitor.trackError(error, context);
export const trackUserAction = (action, details) => productionMonitor.trackUserAction(action, details);
export const trackPerformance = (metric, value) => productionMonitor.trackPerformance(metric, value); 